import React from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip,
  BottomNavigation, BottomNavigationAction, Paper
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  SpaceDashboardRounded as TodayIcon,
  InsightsRounded as PredictionsIcon,
  CompareArrowsRounded as ArbitrageIcon,
  SensorsRounded as LiveIcon,
  QueryStatsRounded as PerformanceIcon,
  LogoutRounded as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { tokens } from '../theme';

export const NAV = [
  { label: 'Today', short: 'Today', icon: <TodayIcon />, path: '/' },
  { label: 'Predictions', short: 'Picks', icon: <PredictionsIcon />, path: '/predictions' },
  { label: 'Arbs & value', short: 'Bets', icon: <ArbitrageIcon />, path: '/arbitrage' },
  { label: 'Live scores', short: 'Live', icon: <LiveIcon />, path: '/livescores' },
  { label: 'Performance', short: 'Stats', icon: <PerformanceIcon />, path: '/stats' },
];

const SIDEBAR = 248;

export function Logo({ size = 32 }) {
  return (
    <Box display="flex" alignItems="center" gap={1.25}>
      <Box sx={{
        width: size, height: size, borderRadius: size / 3.5, display: 'grid', placeItems: 'center',
        background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.outcome.away})`,
        color: '#0B0F14', fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: size * 0.5,
      }}>
        O
      </Box>
      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em' }}>
        Odds <Box component="span" color="primary.main">Predictor</Box>
      </Typography>
    </Box>
  );
}

const activePath = (pathname) => {
  if (pathname === '/history') return '/stats';
  if (pathname === '/value') return '/arbitrage';
  return NAV.find(n => n.path === pathname)?.path || '/';
};

export default function AppShell({ children, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = activePath(pathname);

  // Start each page at the top
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <Box minHeight="100vh" display="flex">
      {/* Desktop sidebar */}
      <Box component="nav" sx={{
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column', width: SIDEBAR, flexShrink: 0,
        position: 'sticky', top: 0, height: '100vh', borderRight: `1px solid ${tokens.border}`, px: 2, py: 2.5,
        bgcolor: tokens.bg,
      }}>
        <Box px={1} mb={4}><Logo /></Box>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {NAV.map(item => {
            const active = current === item.path;
            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                aria-current={active ? 'page' : undefined}
                sx={{
                  borderRadius: 2.5, py: 1,
                  color: active ? 'text.primary' : 'text.secondary',
                  bgcolor: active ? alpha(tokens.accent, 0.14) : 'transparent',
                  '&:hover': { bgcolor: active ? alpha(tokens.accent, 0.18) : alpha('#fff', 0.04) },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: active ? 'primary.main' : 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
              </ListItemButton>
            );
          })}
        </List>
        <Box mt="auto">
          <ListItemButton onClick={onLogout} sx={{ borderRadius: 2.5, color: 'text.secondary' }}>
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Log out" primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
          </ListItemButton>
        </Box>
      </Box>

      <Box flex={1} minWidth={0} display="flex" flexDirection="column">
        {/* Mobile top bar */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 10, px: 2, py: 1.25, pt: 'calc(env(safe-area-inset-top) + 10px)',
          bgcolor: alpha(tokens.bg, 0.85), backdropFilter: 'blur(12px)', borderBottom: `1px solid ${tokens.border}`,
        }}>
          <Logo size={28} />
          <Tooltip title="Log out">
            <IconButton onClick={onLogout} aria-label="Log out" size="small" sx={{ color: 'text.secondary' }}><LogoutIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        <Box component="main" sx={{
          flex: 1, width: '100%', maxWidth: 1240, mx: 'auto',
          px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 2.5, md: 5 },
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 96px)', md: 6 },
        }}>
          {children}
        </Box>
      </Box>

      {/* Mobile bottom navigation */}
      <Paper sx={{
        display: { xs: 'block', md: 'none' }, position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20,
        borderTop: `1px solid ${tokens.border}`, borderRadius: 0, bgcolor: alpha(tokens.surface, 0.92), backdropFilter: 'blur(12px)',
        pb: 'env(safe-area-inset-bottom)',
      }}>
        <BottomNavigation
          showLabels
          value={current}
          onChange={(_, path) => navigate(path)}
          sx={{ bgcolor: 'transparent', height: 64 }}
        >
          {NAV.map(item => (
            <BottomNavigationAction
              key={item.path}
              value={item.path}
              label={item.short}
              icon={item.icon}
              sx={{ minWidth: 0, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' }, '& .MuiBottomNavigationAction-label': { fontSize: 11, fontWeight: 600, mt: 0.25 }, '& .MuiBottomNavigationAction-label.Mui-selected': { fontSize: 11 } }}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
